import { useTranslation } from 'react-i18next';
import { Badge, categoriaLabel } from '@aions/ui-primitives';
import { estadoLabelComite } from '@aions/core';

export interface RecursoCardData {
  caseId: string;
  categoria: string;
  createdWeek: string;
  stateKind: string;
  prazoFimEpochMs: number | null;
  hasRecurso: boolean;
  recursoOrigem: string | null;
  /**
   * Se true, o user logado interpôs este recurso (D-120) — não pode julgar.
   * Calculado pela página do dashboard combinando record.recurso?.interpostoPor
   * com user.sub (o backend não devolve interpostoPor na lista, então o flag
   * fica null por padrão; CaseDetailPage reaplica o check com dado completo).
   */
  selfInterposed?: boolean;
}

interface Props {
  data: RecursoCardData;
  onSelect: (caseId: string) => void;
}

function diasRestantes(prazoFimEpochMs: number | null): number | null {
  if (prazoFimEpochMs === null) return null;
  const ms = prazoFimEpochMs - Date.now();
  return ms > 0 ? Math.ceil(ms / (24 * 3_600 * 1_000)) : 0;
}

/** Card de listagem do dashboard. */
export function RecursoCard({ data, onSelect }: Props): JSX.Element {
  const { t } = useTranslation();
  const dias = diasRestantes(data.prazoFimEpochMs);
  const expirado = dias !== null && dias <= 0;
  const idShort = data.caseId.slice(0, 12);

  const origemLabel =
    data.recursoOrigem === 'denunciante'
      ? t('dashboard.origem_denunciante')
      : data.recursoOrigem === 'denunciado_via_rh'
        ? t('dashboard.origem_denunciado_via_rh')
        : '';

  const categoria = categoriaLabel(t, data.categoria);

  return (
    <button
      type="button"
      className="cf-card cf-card--pad-md cf-card--interactive case-card"
      onClick={() => onSelect(data.caseId)}
      aria-label={`Caso ${idShort} — ${categoria} — ${origemLabel}`}
    >
      {/* Título humano (a categoria) como headline; o caseId truncado vira
          etiqueta mono secundária e discreta (design v2). */}
      <span className="case-card__cat">{categoria}</span>
      <span className="case-card__id">{idShort}…</span>

      <span className="case-card__badges">
        <Badge tone="brand">{estadoLabelComite(data.stateKind)}</Badge>
        {origemLabel && <Badge tone="info">{origemLabel}</Badge>}
        {expirado ? (
          <Badge tone="coral">{t('dashboard.prazo_expirado')}</Badge>
        ) : dias !== null ? (
          <Badge tone="amber">{t('dashboard.prazo_dias_restantes', { count: dias })}</Badge>
        ) : null}
      </span>

      <span className="case-card__meta">{data.createdWeek}</span>

      {data.selfInterposed && (
        <Badge
          tone="coral"
          title="Você apresentou este recurso — quem recorre não pode julgar o próprio recurso."
        >
          ⚠ {t('dashboard.interpostoPor_self')}
        </Badge>
      )}
    </button>
  );
}
